<template>
  <div class="space-y-6">
    <!-- Page header -->
    <div>
      <h1 class="text-3xl font-bold">Symbols</h1>
      <p class="text-muted-foreground">
        Analyze call options with {{ formatCurrency(initialCash) }} initial cash across different symbol lists
      </p>
    </div>

    <!-- Symbol list tabs -->
    <div class="space-y-4">
      <div class="flex space-x-1 rounded-lg bg-muted p-1">
        <button
          v-for="list in symbolLists"
          :key="list.key"
          @click="selectedList = list.key"
          :class="[
            'flex-1 rounded-md px-3 py-2 text-sm font-medium transition-colors',
            selectedList === list.key
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground',
          ]"
        >
          {{ list.name }}
        </button>
      </div>

      <!-- List description -->
      <div class="rounded-md border bg-muted/50 p-4">
        <h3 class="font-medium">{{ getCurrentListDescription() }}</h3>
      </div>

      <!-- Top symbols panel -->
      <TopSymbolsPanel :list-type="selectedList" :limit="500" />
    </div>

    <!-- Quick actions -->
    <div class="flex items-center justify-between rounded-md border p-4">
      <div>
        <h3 class="font-medium">Quick Actions</h3>
        <p class="text-sm text-muted-foreground">
          Navigate to charts or view detailed analysis
        </p>
      </div>
      <div class="flex space-x-2">
        <router-link
          to="/charts"
          class="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4"
        >
          <svg
            class="mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 3v18h18" />
            <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3" />
          </svg>
          View Charts
        </router-link>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { apiClient } from '@/lib/api'
import { useInitialCash } from '@/lib/initialCash'
import { formatCurrency } from '@/lib/utils'
import TopSymbolsPanel from '@/components/TopSymbolsPanel.vue'

const { initialCash } = useInitialCash()

const symbolLists = ref([
  { key: 'sp500', name: 'S&P 500' },
  { key: 'nasdaq100', name: 'NASDAQ 100' },
  { key: 'etfs', name: 'Sector ETFs' },
  { key: 'high_volume', name: 'High Volume' },
  { key: 'combined', name: 'Combined' }
])

const selectedList = ref('sp500')
const listDescriptions = ref<Record<string, string>>({})

const getCurrentListDescription = () => {
  return listDescriptions.value[selectedList.value] || 'Loading description...'
}

const fetchListDescriptions = async () => {
  try {
    const data = await apiClient.getSymbolLists()
    listDescriptions.value = data.descriptions
  } catch (error) {
    console.error('Failed to fetch list descriptions:', error)
  }
}

onMounted(() => {
  fetchListDescriptions()
})
</script>
